<script>
  import { mapActions } from 'vuex';
  import { Carousel, Slide } from "vue-carousel";
  import UploadPhoto from "@/components/widgets/upload-photo";

  export default {
    name: 'PhotoManager',
    props: {
      manageFor: {
        type: String,
        required: true
      },
      photos: {
        type: Array,
        required: true
      }
    },
    data() {
      return {
        currentSlide: 1,
        pageSize: 5,
        pagingOffset: 3
      }
    },
    methods: {
      ...mapActions("property", { deletePropertyPhoto: "deletePhoto" }),
      ...mapActions("area", { deleteAreaPhoto: "deletePhoto" }),
      ...mapActions("item", { deleteItemPhoto: "deletePhoto" }),
      setPage() {
        let nextPage;

        if (this.currentSlide <= this.pagingOffset)
          nextPage = 0;
        else if (this.currentSlide <= this.pagingEnd)
          nextPage = this.currentSlide - this.pagingOffset;
        else
          nextPage = this.pagingEnd - this.pagingOffset;

        this.$refs.thumbnailCarousel.goToPage(nextPage);
      },
      onPhotoSelect({ index }) {
        this.currentSlide = parseInt(index) + 1;
        this.setPage();
      },
      onDeletedPhoto() {
        if (this.currentSlide != 1) {
          this.currentSlide -= 1;
          this.setPage();
        }
      },
      onPhotoUploaded() {
        this.currentSlide = this.photos.length + 1;
        this.$refs.thumbnailCarousel.goToLastSlide();
      },
      deletePhoto(){
        const args = { id: this.viewedPhoto.id, callback: this.onDeletedPhoto };

        if(this.manageFor == "property")
          this.deletePropertyPhoto(args);
        else if(this.manageFor == "area")
          this.deleteAreaPhoto(args);
        else if(this.manageFor == "item")
          this.deleteItemPhoto(args);
      },
       prevPhoto() {
        if (this.currentSlide > 1) {
          this.currentSlide -= 1;
          this.setPage();
        }
      },
      // next photo navigation method
      nextPhoto() {
        if (this.currentSlide < this.photos.length) {
          this.currentSlide += 1;
          this.setPage();
        }
      },
    },
    computed: {
      viewedPhoto() {
        return this.photos[this.currentSlide - 1];
      },
      pagingEnd() {
        return this.photos.length - this.pagingOffset + 1;
      }
    },
    components: { Carousel, Slide, UploadPhoto }
  }
</script>

<style>
  .main {
    margin-bottom: 30px;
  }
  .thumbnails {
    margin: 0 -5px;
    width: calc(100% + 10px);
  }
  .slide {
    align-items: center;
    box-sizing: border-box;
    color: #fff;
    display: flex;
    justify-content: center;
    height: 450px;
    width: 100%;
  }
  .slide img {
    -o-object-fit: cover;
       object-fit: cover;
    -o-object-position: center;
       object-position: center;
    max-height: 100%;
    max-width: 100%;
  }
  .slide--thumbnail {
    cursor: pointer;
    padding: 0 5px;
    transition: opacity 0.3s;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
  }
  .slide--thumbnail img {
    height: 100px;
  }
  .slide--thumbnail:not(.is-active):hover {
    opacity: 0.75;
  }
  .slide--thumbnail.is-active {
    opacity: 0.75;
  }
  .slide--thumbnail i {
    margin-top: 5px;
    height: 15px;
    font-size: 10px
  }
  .slide--thumbnail:not(.is-active) i {
    color: #fff;
  }
  .slide--thumbnail.is-active i {
    color: #79829c;
  }
  .next-label {
    font-size: 25px;
    margin-right: 10px;
    color: #79829c;
  }
  .prev-label {
    font-size: 25px;
    margin-left: 10px;
    color: #79829c;
  }
  .next-label.disabled,
  .prev-label.disabled {
    color: #ccc;
    cursor: not-allowed;
  }
</style>
<template>
  <div style="width: 99%;">
    <div v-if="this.viewedPhoto" class="card">
      <div class="card-body">
        <!-- Move navigation arrows inside the main image container -->
        <div class="main" style="position: relative;">
          <!-- Navigation arrow on the left side -->
          <i class="fas fa-chevron-left next-label"
             :class="{ 'disabled': currentSlide === 1 }"
             style="position: absolute; top: 50%; left: 10px; transform: translateY(-50%);"
             @click="prevPhoto"></i>
          <div class="slide">
            <img :src="viewedPhoto.image_url"/>
          </div>
          <!-- Navigation arrow on the right side -->
          <i class="fas fa-chevron-right prev-label"
             :class="{ 'disabled': currentSlide === photos.length }"
             style="position: absolute; top: 50%; right: 10px; transform: translateY(-50%);"
             @click="nextPhoto"></i>
        </div>
        <div class="thumbnails">
          <Carousel
            ref="thumbnailCarousel"
            :perPage="pageSize"
            :scrollPerPage="false"
            navigationNextLabel="<i class='fas fa-chevron-right prev-label'></i>"
            navigationPrevLabel="<i class='fas fa-chevron-left next-label'></i>"
            :navigationEnabled="true"
            :paginationEnabled="false"
            :loop="true">
            <slide
              v-for="(photo, index) in photos"
              :key="index"
              :data-index="index"
              @slide-click="onPhotoSelect"
              class="slide--thumbnail"
              :class="{ 'is-active': currentSlide == index + 1 }">
              <img :src="photo.image_url" />
              <i class="fas fa-solid fa-circle"></i>
            </slide>
          </Carousel>
        </div>
      </div>
    </div>
    <div v-else>
      No existing photos for {{ manageFor }}
    </div>
    <b-row>
      <b-col class="d-flex justify-content-end" v-if="$can('update', 'Property')">
        <b-button v-b-modal.modal-addPhoto variant="primary">Add Photo</b-button>
        <b-button v-if="this.viewedPhoto" class="ms-3" v-b-modal.modal-delete variant="danger">Delete Photo</b-button>
      </b-col>
    </b-row>
    <UploadPhoto :uploadFor="this.manageFor" @photo-uploaded="onPhotoUploaded()" />
    <b-modal id="modal-delete" title="Delete Photo" @ok="deletePhoto">
      Are you sure?
    </b-modal>
  </div>
</template>
